import React, {useCallback, useEffect, useRef, useState} from "react"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro"

import Constants from "../../constants"
import HttpManager from "../../services/HttpManager"
import {getErrorMessage} from "../../common"

export interface StudentNotesProps
{
	studentId : number
	campusId  : number
	canWrite  : boolean
}

interface Nota
{
	id            : number
	note          : string
	createdBy     : string | null
	createdAt     : string | null
	updatedAt     : string | null
	legacy        : boolean
	edited        : boolean
	editable      : boolean
	editableUntil : string | null
}

//Fecha y hora al minuto, que es lo que hace falta para saber cuándo se escribió algo.
const fechaYHora = (valor : string | null) : string =>
{
	if(!valor)
	{
		return "-"
	}

	const fecha = new Date(valor)

	if(isNaN(fecha.getTime()))
	{
		return "-"
	}

	return fecha.toLocaleString
	(
		"es",
		{day : "2-digit", month : "2-digit", year : "numeric", hour : "2-digit", minute : "2-digit"}
	)
}

/**
 * Observaciones del alumno como bitácora (Esteban, 2026-09-11): cada una queda firmada con
 * quién la escribió y a qué hora, no se borran nunca, y solo su autor puede corregirla
 * dentro de los primeros minutos.
 */
const StudentNotes : React.FC<StudentNotesProps> = ({studentId, campusId, canWrite}) =>
{
	const http = HttpManager.getInstance()
	const [notas, setNotas] = useState<Nota[] | null>(null)
	const [ventana, setVentana] = useState<number>(10)
	const [nueva, setNueva] = useState<string>("")
	const [enEdicion, setEnEdicion] = useState<number | null>(null)
	const [textoEdicion, setTextoEdicion] = useState<string>("")
	const [ocupado, setOcupado] = useState<boolean>(false)
	const [faltaTexto, setFaltaTexto] = useState<boolean>(false)
	const cajaTexto = useRef<HTMLTextAreaElement>(null)
	const [error, setError] = useState<string | null>(null)

	const cargar = useCallback
	(
		()=>
		{
			setError(null)
			http.studentService.getStudentNotes(campusId, studentId)
				.then
				(
					(respuesta : any)=>
					{
						setNotas(respuesta.data.notes || [])
						setVentana(respuesta.data.editWindowMinutes || 10)
					}
				)
				.catch((e : any) => {setNotas([]); setError(getErrorMessage(e))})
		},
		[campusId, studentId]
	)

	useEffect(() => {cargar()}, [cargar])

	const agregar = async ()=>
	{
		//Antes el botón salía deshabilitado y el clic no hacía nada: nadie entendía por qué
		//(Esteban, 2026-09-12). Ahora se dice qué falta y se lleva el cursor a la caja.
		if(nueva.trim() === "")
		{
			setFaltaTexto(true)
			cajaTexto.current?.focus()

			return
		}

		setFaltaTexto(false)
		setOcupado(true)
		setError(null)

		try
		{
			await http.studentService.addStudentNote(campusId, studentId, nueva.trim())
			setNueva("")
			cargar()
		}
		catch(e : any)
		{
			setError(getErrorMessage(e))
		}
		finally
		{
			setOcupado(false)
		}
	}

	const guardarEdicion = async (nota : Nota)=>
	{
		if(textoEdicion.trim() === "")
		{
			return
		}

		setOcupado(true)
		setError(null)

		try
		{
			await http.studentService.updateStudentNote(campusId, studentId, nota.id, textoEdicion.trim())
			setEnEdicion(null)
			setTextoEdicion("")
			cargar()
		}
		catch(e : any)
		{
			setError(getErrorMessage(e))
		}
		finally
		{
			setOcupado(false)
		}
	}

	return <div style={{textAlign : "left"}}>
		{error ? <div className="alert alert-danger" style={{fontSize : "13px"}}>{error}</div> : ""}
		{
			!canWrite ? "" : <div style={{marginBottom : "10px"}}>
				<textarea
				  ref={cajaTexto}
				  className={"form-control" + (faltaTexto ? " is-invalid" : "")}
				  style={{minHeight : "70px"}}
				  maxLength={2000}
				  disabled={ocupado}
				  placeholder={Constants.Common.newNotePlaceholder}
				  value={nueva}
				  onChange={(evento : any) => {setNueva(evento.target.value); setFaltaTexto(false)}}
				  onKeyDown={(evento : any) => {if((evento.metaKey || evento.ctrlKey) && evento.key === "Enter"){agregar()}}}
				/>
				{
					!faltaTexto ? "" : <div style={{fontSize : "12px", color : "var(--wa-rojo)", marginTop : "4px"}}>
						{Constants.Common.noteTextRequired}
					</div>
				}
				<div style={{display : "flex", justifyContent : "flex-end", alignItems : "center", marginTop : "6px", gap : "10px"}}>
					<button
					  type="button"
					  className="btn btn-primary btn-sm rounded-pill"
					  disabled={ocupado}
					  onClick={agregar}
					>
						<FontAwesomeIcon icon={solid("plus")} />
						&nbsp;
						{Constants.Common.addNote}
					</button>
				</div>
			</div>
		}
		{
			notas === null
					?
				<div className="loader" />
					:
				<>
					{
						notas.length < 1
								?
							<div style={{color : "var(--wa-apagado)", fontSize : "13px"}}>
								{Constants.Common.noNotesYet}
							</div>
								:
							""
					}
					{
						notas.map
						(
							(nota : Nota) => <div key={"nota_" + nota.id} className="card" style=
							{{
								borderLeft : "4px solid " + (nota.legacy ? "var(--wa-filo)" : "var(--wa-acento)"),
								padding    : "8px 12px",
								marginBottom : "8px"
							}}>
								<div style={{fontSize : "12px", color : "var(--wa-apagado)", display : "flex", flexWrap : "wrap", gap : "6px"}}>
									{
										nota.legacy
												?
											<span>{Constants.Common.legacyNote}</span>
												:
											<>
												<b>{nota.createdBy || Constants.Common.unknownAuthor}</b>
												<span>·</span>
												<span>{nota.createdAt ? fechaYHora(nota.createdAt) : Constants.Common.unknownDate}</span>
												{nota.edited ? <span>· {Constants.Common.noteEdited}</span> : ""}
											</>
									}
								</div>
								{
									enEdicion === nota.id
											?
										<div style={{marginTop : "6px"}}>
											<textarea
											  className="form-control"
											  style={{minHeight : "70px"}}
											  maxLength={2000}
											  disabled={ocupado}
											  value={textoEdicion}
											  onChange={(evento : any) => setTextoEdicion(evento.target.value)}
											/>
											<div style={{display : "flex", gap : "8px", justifyContent : "flex-end", marginTop : "6px"}}>
												<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill"
												  disabled={ocupado} onClick={() => {setEnEdicion(null); setTextoEdicion("")}}
												>
													{Constants.Common.close}
												</button>
												<button type="button" className="btn btn-primary btn-sm rounded-pill"
												  disabled={ocupado} onClick={() => guardarEdicion(nota)}
												>
													{Constants.Common.saveChanges}
												</button>
											</div>
										</div>
											:
										<>
											<div style={{whiteSpace : "pre-wrap", marginTop : "4px"}}>
												{nota.note}
											</div>
											{
												!nota.editable ? "" : <div style={{marginTop : "4px"}}>
													<button type="button" className="btn btn-link btn-sm"
													  style={{padding : "0px", fontSize : "12px"}}
													  onClick={() => {setEnEdicion(nota.id); setTextoEdicion(nota.note)}}
													>
														<FontAwesomeIcon icon={solid("pen")} />
														&nbsp;
														{Constants.Common.correctNote}
													</button>
												</div>
											}
										</>
								}
							</div>
						)
					}
				</>
		}
	</div>
}

export default StudentNotes
